// "The shelf right now" tabbed product rail.
//
// A tab does three things at once, which is why this is one controller and
// not a tab widget bolted to a scroller: it swaps the visible panel, it
// rewrites the heading above the rail with that tab's own headline, and it
// returns the rail to its start so the new selection is read from the
// beginning rather than from wherever the previous one had been scrolled to.
//
// The scrolling itself -- overflow-aware arrows, whole-item paging and the
// mobile scroll-position track -- is kludi-rail.js, the one rail controller
// this storefront has. All this class adds is the tabs, and the one thing
// only it knows: which of its several rows is the live one.
class KludiProductRail extends window.KludiRail {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[data-kludi-rail-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-kludi-rail-panel]'));
    this.heading = this.querySelector('[data-kludi-rail-heading]');

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab));
      tab.addEventListener('keydown', (event) => this._onTabKeydown(event, tab));
    });

    // the theme editor selects a block rather than clicking its tab, so the
    // panel it just revealed has to follow the selection
    this.addEventListener('shopify:block:select', (event) => {
      const tab = this.tabs.find((candidate) => candidate.contains(event.target) || candidate === event.target);
      if (tab) this.select(tab);
    });

    this._applyPreOrderLabels();
    this.initRail();
  }

  get activePanel() {
    return this.panels.find((panel) => !panel.hidden) || null;
  }

  // the rail's own contract, answered against the panel on show rather than
  // against the first row in the DOM
  get railRow() {
    return this.activePanel?.querySelector('[data-kludi-rail-row]') || null;
  }

  select(tab) {
    if (!this.tabs.includes(tab)) return;

    this.tabs.forEach((candidate) => {
      const selected = candidate === tab;
      candidate.setAttribute('aria-selected', selected ? 'true' : 'false');
      candidate.tabIndex = selected ? 0 : -1;
    });

    this.panels.forEach((panel) => {
      panel.hidden = panel.getAttribute('aria-labelledby') !== tab.id;
    });

    if (this.heading && tab.dataset.heading) {
      this.heading.textContent = tab.dataset.heading;
    }

    // the newly shown rail starts at its beginning, matching the design --
    // 'auto' rather than the row's own smooth behaviour, because the rail is
    // being revealed rather than travelled through
    const row = this.railRow;
    if (row) row.scrollTo({ left: 0, behavior: 'auto' });

    this.refreshRail();
  }

  // roving tabindex: the tab strip is one stop in the tab order and the
  // arrow keys move between the tabs inside it
  _onTabKeydown(event, tab) {
    const step = { ArrowRight: 1, ArrowLeft: -1, Home: 'first', End: 'last' }[event.key];
    if (step === undefined) return;

    event.preventDefault();
    const index = this.tabs.indexOf(tab);
    let target;
    if (step === 'first') target = this.tabs[0];
    else if (step === 'last') target = this.tabs[this.tabs.length - 1];
    else target = this.tabs[(index + step + this.tabs.length) % this.tabs.length];

    target.focus();
    this.select(target);
  }

  // the card component has no pre-order state of its own, so the two labels a
  // pre-order tab changes are rewritten here from attributes the section filled
  // in through `t` -- no English is written in this file.
  //
  // data-in-stock is set as well as the text, and that is the part that makes
  // it stick: the card's own finish switcher rewrites its stock line from that
  // attribute on every variant change (kludi-card-product.js), so writing only
  // the text node would last until the first finish click and then silently
  // revert to "In stock". Setting the attribute uses the card's own documented
  // contract rather than fighting it.
  //
  // data-sold-out is deliberately left alone, and a card already showing the
  // sold-out state keeps it: a variant with no stock at all is sold out
  // whether or not its tab is a pre-order one.
  //
  // The button's two label spans are written individually rather than through
  // its textContent, which would collapse the card's own long/short breakpoint
  // pair into a single text node.
  _applyPreOrderLabels() {
    const stockLabel = this.dataset.preOrderStock;
    const ctaLabel = this.dataset.preOrderCta;
    if (!stockLabel || !ctaLabel) return;

    this.querySelectorAll('[data-kludi-rail-panel][data-pre-order] kludi-card').forEach((card) => {
      card.dataset.inStock = stockLabel;

      const stock = card.querySelector('.kludi-card__stock');
      if (stock && !stock.classList.contains('kludi-card__stock--out')) {
        stock.textContent = stockLabel;
      }

      card.querySelectorAll('.kludi-card__cta-label--long, .kludi-card__cta-label--short').forEach((label) => {
        label.textContent = ctaLabel;
      });
    });
  }
}

window.customElements.define('kludi-product-rail', KludiProductRail);
